import React, { useRef, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFamilyTree } from './FamilyTreeProvider';
import TreeEdge from './TreeEdge';
import TreeNode from './TreeNode';
import { buildParentChildPath, buildSpousePath } from './edgePaths';

/**
 * FamilyTreeCanvas - Canvas with pan, zoom, and family tree rendering
 */
const FamilyTreeCanvas = ({ onNodeClick }) => {
  const {
    graph,
    layout,
    showSpouses,
    pan,
    zoom,
    isPanning,
    setIsPanning,
    updatePan,
    updateZoom,
    containerRef,
  } = useFamilyTree();

  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const rafPanRef = useRef(null);

  // Local pan state for smooth panning - only syncs on mouse up
  const [localPan, setLocalPan] = useState(null);
  const panX = localPan?.panX ?? pan.x;
  const panY = localPan?.panY ?? pan.y;

  // Ordered nodes for rendering
  const orderedNodes = useMemo(() => {
    const idsInOrder = layout.levels.flatMap((lvl) => lvl);
    return idsInOrder.map((id) => graph.nodesById.get(id)).filter(Boolean);
  }, [graph.nodesById, layout.levels]);

  // Edge paths
  const parentChildEdgePaths = useMemo(() => {
    const { positions, svg } = layout;
    return graph.parentChildEdges
      .map((e) => {
        const fromPos = positions.get(e.fromId);
        const toPos = positions.get(e.toId);
        const d = buildParentChildPath(fromPos, toPos, svg.verticalGap);
        return {
          key: `${e.fromId}->${e.toId}`,
          d,
        };
      })
      .filter((e) => !!e.d);
  }, [graph.parentChildEdges, layout]);

  const spouseEdgePaths = useMemo(() => {
    const { positions } = layout;
    return graph.spouseEdges
      .map((e) => {
        const aPos = positions.get(e.aId);
        const bPos = positions.get(e.bId);
        const d = buildSpousePath(aPos, bPos);
        return {
          key: `${e.aId}<->${e.bId}`,
          d,
        };
      })
      .filter((e) => !!e.d);
  }, [graph.spouseEdges, layout]);

  // Handle wheel for zooming
  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate zoom
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.max(0.1, Math.min(5, zoom * delta));

      // Adjust pan to zoom towards mouse position
      const zoomRatio = newZoom / zoom;
      const newPanX = mouseX - (mouseX - panX) * zoomRatio;
      const newPanY = mouseY - (mouseY - panY) * zoomRatio;

      updatePan(newPanX, newPanY);
      updateZoom(newZoom);
    },
    [zoom, panX, panY, updatePan, updateZoom, containerRef]
  );

  // Handle mouse down
  const handleMouseDown = useCallback(
    (e) => {
      const { target } = e;
      const isNode = target.closest('g[role="button"]') !== null;
      const isEdge = target.tagName === 'path';

      // Support middle mouse button (button 1) for panning anywhere
      if (e.button === 1) {
        e.preventDefault();
        setIsPanning(true);
        panStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          panX: pan.x,
          panY: pan.y,
        };
        return;
      }

      // Only pan with left mouse button if clicking on background, not on nodes or edges
      if (e.button === 0 && !isNode && !isEdge) {
        setIsPanning(true);
        panStartRef.current = {
          x: e.clientX,
          y: e.clientY,
          panX: pan.x,
          panY: pan.y,
        };
      }
    },
    [pan, setIsPanning]
  );

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e) => {
      // Only pan if we're actively panning (mouse button held down)
      if (!isPanning) {
        return;
      }

      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      const newPanX = panStartRef.current.panX + dx;
      const newPanY = panStartRef.current.panY + dy;

      if (rafPanRef.current) {
        cancelAnimationFrame(rafPanRef.current);
      }
      rafPanRef.current = requestAnimationFrame(() => {
        setLocalPan({ panX: newPanX, panY: newPanY });
        rafPanRef.current = null;
      });
    },
    [isPanning]
  );

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    // Cancel any pending pan animation
    if (rafPanRef.current) {
      cancelAnimationFrame(rafPanRef.current);
      rafPanRef.current = null;
    }
    // Sync local pan to state on mouse up
    if (localPan) {
      updatePan(localPan.panX, localPan.panY);
      setLocalPan(null);
    }
    setIsPanning(false);
  }, [localPan, updatePan, setIsPanning]);

  // Add window-level mouse up handler to ensure panning stops even if mouse is released outside
  useEffect(() => {
    const handleWindowMouseUp = () => {
      setIsPanning((prevIsPanning) => {
        if (prevIsPanning) {
          // Cancel any pending pan animation
          if (rafPanRef.current) {
            cancelAnimationFrame(rafPanRef.current);
            rafPanRef.current = null;
          }
        }
        return false;
      });
    };

    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
    // setIsPanning is stable, but included to satisfy exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsPanning]);

  // Attach wheel event listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
    // containerRef is stable, but included to satisfy exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleWheel, containerRef]);

  // SVG viewBox
  const viewBox = useMemo(() => {
    const w = layout.svg?.width || 1;
    const h = layout.svg?.height || 1;
    return `0 0 ${w} ${h}`;
  }, [layout.svg]);

  const getCursor = () => {
    if (isPanning) return 'grabbing';
    return 'default';
  };

  return (
    <div
      ref={containerRef}
      role="presentation"
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: 'rgba(2, 6, 23, 0.98)',
        borderRadius: 12,
        position: 'relative',
        cursor: getCursor(),
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onContextMenu={(e) => {
        // Prevent context menu on right-click when panning
        if (isPanning) {
          e.preventDefault();
        }
      }}
    >
      {/* SVG layer - uses CSS transform on wrapper div for pan/zoom like corkboard */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          transformOrigin: '0 0',
          transform: `translate3d(${panX}px, ${panY}px, 0) scale(${zoom})`,
          willChange: 'transform',
          backfaceVisibility: 'hidden',
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox={viewBox}
          preserveAspectRatio="xMidYMid meet"
          style={{
            display: 'block',
            textRendering: 'geometricPrecision',
            shapeRendering: 'geometricPrecision',
          }}
        >
          {/* Edges first so nodes render on top */}
          <g>
            {parentChildEdgePaths.map((e) => (
              <TreeEdge key={e.key} d={e.d} kind="parentChild" />
            ))}
          </g>

          {showSpouses && (
            <g>
              {spouseEdgePaths.map((e) => (
                <TreeEdge key={e.key} d={e.d} kind="spouse" />
              ))}
            </g>
          )}

          <g>
            {orderedNodes.map((n) => (
              <TreeNode
                key={n._id}
                character={n}
                position={layout.positions.get(n._id)}
                onClick={onNodeClick}
              />
            ))}
          </g>
        </svg>
      </div>
    </div>
  );
};

FamilyTreeCanvas.propTypes = {
  onNodeClick: PropTypes.func,
};

FamilyTreeCanvas.defaultProps = {
  onNodeClick: null,
};

export default FamilyTreeCanvas;
